import React from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import useCatalogPicker from '../../../hooks/checkouts/useCatalogPicker';
import useAddOrderItem from '../../../hooks/checkouts/useAddOrderItem';
import useAddCustomLine from '../../../hooks/checkouts/useAddCustomLine';
import { CatalogProduct, CatalogVariant } from '../../../types/catalog';
import {
  ProductInView,
  variantDisplayName,
} from '../../../utils/catalog/catalogFilters';
import CatalogSearchBar from './CatalogSearchBar';
import CustomLineRow from './CustomLineRow';
import CatalogDirectory, { DirectoryLevel } from './CatalogDirectory';
import FilterChips from './FilterChips';
import ProductList from './ProductList';
import VariantChooser from './VariantChooser';

const TWO_PANE_QUERY = '(min-width:768px)';
const PANE_HEIGHT = 500;

interface Props {
  onAdded: () => void;
}

type OpenProduct = {
  product: CatalogProduct;
  variants: CatalogVariant[];
};

export default function CatalogPicker({ onAdded }: Props) {
  const picker = useCatalogPicker(true);
  const addOrderItem = useAddOrderItem();
  const addCustomLine = useAddCustomLine();
  const twoPane = useMediaQuery(TWO_PANE_QUERY);
  const [level, setLevel] = React.useState<DirectoryLevel>(
    picker.selection.category ? 'category' : 'categories',
  );
  const [scrollEl, setScrollEl] = React.useState<HTMLElement | null>(null);
  const [openProduct, setOpenProduct] = React.useState<OpenProduct | null>(
    null,
  );

  const add = (product: CatalogProduct, variant: CatalogVariant, qty = 1) => {
    addOrderItem(
      {
        itemId: variant.id,
        name: variantDisplayName(product, variant),
        price: variant.price,
        category: product.category,
      },
      qty,
    );
    setOpenProduct(null);
    onAdded();
  };

  const onSelectProduct = ({ product, variants }: ProductInView) => {
    if (variants.length === 1) {
      add(product, variants[0]);
      return;
    }
    setOpenProduct({ product, variants });
  };

  if (picker.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (picker.isError) {
    return (
      <Typography variant="body2" color="error" textAlign="center" py={4}>
        The catalog could not be loaded.
      </Typography>
    );
  }

  const chooser = openProduct && (
    <VariantChooser
      key={openProduct.product.id}
      product={openProduct.product}
      variants={openProduct.variants}
      attributes={picker.attributes}
      onBack={() => setOpenProduct(null)}
      onAdd={(variant, qty) => add(openProduct.product, variant, qty)}
    />
  );

  const directory = (
    <CatalogDirectory
      picker={picker}
      level={level}
      onLevel={setLevel}
      onNavigate={() => setOpenProduct(null)}
    />
  );

  const searching = !!picker.query.trim();
  const setQuery = (value: string) => {
    setOpenProduct(null);
    picker.setQuery(value);
  };
  const addCustom = () => {
    addCustomLine(picker.query);
    picker.setQuery('');
    onAdded();
  };
  const searchHeader = (
    <Stack gap={1}>
      <CatalogSearchBar value={picker.query} onChange={setQuery} />
      <CustomLineRow query={picker.query} onAdd={addCustom} />
    </Stack>
  );

  const list = searching ? (
    <ProductList
      products={picker.searchResults}
      selectedProductId={openProduct?.product.id ?? null}
      onSelect={onSelectProduct}
      scrollParent={scrollEl}
    />
  ) : picker.selection.category ? (
    <Stack gap={1.5}>
      <FilterChips
        chips={picker.chips}
        onToggleValue={picker.toggleAttributeValue}
        onClear={picker.clearAttribute}
      />
      <ProductList
        products={picker.products}
        selectedProductId={openProduct?.product.id ?? null}
        onSelect={onSelectProduct}
        scrollParent={scrollEl}
      />
    </Stack>
  ) : (
    <Typography
      variant="body2"
      color="text.secondary"
      textAlign="center"
      py={6}>
      Pick a category or search to find an item.
    </Typography>
  );

  if (twoPane) {
    return (
      <Stack gap={1.5}>
        {searchHeader}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 2fr) 3fr',
            gap: 2,
            height: PANE_HEIGHT,
          }}>
          <Box
            sx={{
              overflowY: 'auto',
              pr: 1,
              borderRight: '1px solid',
              borderColor: 'divider',
            }}>
            {directory}
          </Box>
          <Box ref={setScrollEl} sx={{ overflowY: 'auto' }}>
            {chooser ?? list}
          </Box>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap={1.5}>
      {searchHeader}
      <Box ref={setScrollEl} sx={{ maxHeight: PANE_HEIGHT, overflowY: 'auto' }}>
        {chooser ?? (
          <Stack gap={1.5}>
            {directory}
            {(level === 'category' || searching) && list}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
